import type { CaseData, CMSData, CmsPage } from "@/cms/models";

/** Sagskontakt's keys (CaseContactSection). Every one is optional; without `contactInfo` the older switches decide. */
export interface CaseContactSettings {
  showAgent?: boolean;
  showResourceGroupDisplayName?: boolean;
  useResourceGroupPhoneNumber?: boolean;
  useResourceGroupEmail?: boolean;
  downloadFormTextColor?: string;
  employeeImageAspectRatio?: string;
  /** Beside the form: the case's agent, the store, or nothing. */
  contactInfo?: CaseContactInfo;
  /** The form's largest width, on the section width scale; absent is all the room there is. */
  formWidth?: string;
}

export const CASE_CONTACT_INFO = ["agent", "store", "none"] as const;
export type CaseContactInfo = (typeof CASE_CONTACT_INFO)[number];

const FORM_WIDTHS: Record<string, string> = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
  "4xl": "1880px",
};

/**
 * What stands beside the form. A chosen »agent« falls back to the store on a case without an agent. Without a known
 * choice the section shows the agent where it, the page or the website asks for agents (the page's own »Vis mægler«
 * before the website's), as it always has.
 */
export function caseContactInfo(
  specifics: CaseContactSettings | undefined,
  caseData: Pick<CaseData, "agent">,
  pageData: Pick<CmsPage, "showAgent"> | undefined,
  cmsData: Pick<CMSData, "showAgents">,
): CaseContactInfo {
  const chosen = specifics?.contactInfo;
  if (chosen === "none" || chosen === "store") return chosen;
  const wanted =
    chosen === "agent" ||
    !!specifics?.showAgent ||
    !!pageData?.showAgent ||
    (typeof pageData?.showAgent === "undefined" && !!cmsData.showAgents);
  return caseData.agent && wanted ? "agent" : "store";
}

/** The form's max-width in CSS, or undefined for an absent or unknown token. */
export function caseContactFormWidth(value: unknown): string | undefined {
  return typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(FORM_WIDTHS, value)
    ? FORM_WIDTHS[value]
    : undefined;
}
